import type { AgenticIntegration } from "@p0security/integrations-agentic-shared/components";
import { gatewayOauthEndpointOf } from "@p0security/integrations-agentic-shared/domain-hosting";
import { gatewayPathUrl } from "@p0security/integrations-agentic-shared/url";
import {
  installedItem,
  installedItems,
} from "@p0security/shared/install/installed";
import { compact } from "lodash-es";

import { urlOf } from "./url";

/** An installed MCP server whose upstream credential is obtained over OAuth. */
export type OauthServer = {
  /** The OAuth endpoint of the gateway that hosts the server. */
  endpoint: URL;
  id: string;
};

/** Lists the installed MCP servers a client can register an OAuth credential
 * for. */
export const toOauthServers = (config: AgenticIntegration): OauthServer[] =>
  compact(
    Object.entries(installedItems("server", config)).map(([id, server]) => {
      // Guards against a stored server item that has no `credential`.
      if (server?.credential?.type !== "oauth") return undefined;
      // A gateway that is not installed, or has not finished domain-hosting
      // setup, cannot serve the registration page.
      const endpoint = urlOf(
        gatewayOauthEndpointOf(installedItem("gateway", config, server.gateway))
      );
      return endpoint && { endpoint, id };
    })
  );

/** The gateway page that binds an upstream OAuth credential to a client.
 *
 * Any path prefix on `endpoint` is kept, since a self-hosted gateway may serve
 * its OAuth routes below one.
 */
export const oauthRegistrationUrl = (
  endpoint: URL,
  { backendId, clientId }: { backendId: string; clientId: string }
) => {
  // Rebuilt from a parsed URL, so this cannot throw.
  const url = new URL(
    gatewayPathUrl(
      `${endpoint.origin}${endpoint.pathname}`,
      "oauth-registration"
    )
  );
  url.search = new URLSearchParams({
    backend_id: backendId,
    client_id: clientId,
  }).toString();
  return url.toString();
};
